import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  dismissNotification,
  fetchAgentActionDetail,
  fetchAgentActionFeed,
  fetchAgentDependencies,
  fetchNotifications,
} from '../api';
import type { TimeRange } from '../types';

const POLL_MS = 30_000;

export function useAgentActionFeed(appId: string | null, range: TimeRange) {
  return useQuery({
    queryKey: ['agent-actions', 'feed', appId, range],
    queryFn: () => fetchAgentActionFeed({ app_id: appId, range }),
    refetchInterval: POLL_MS,
    staleTime: 10_000,
  });
}

export function useAgentActionDetail(id: string | null) {
  return useQuery({
    queryKey: ['agent-actions', 'detail', id],
    queryFn: () => fetchAgentActionDetail(id!),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
}

export function useAgentDependencies(appId: string | null) {
  return useQuery({
    queryKey: ['agent-actions', 'dependencies', appId],
    queryFn: () => fetchAgentDependencies(appId!),
    enabled: Boolean(appId),
    staleTime: 5 * 60_000,
  });
}

export function useNotifications(appId?: string | null) {
  return useQuery({
    queryKey: ['agent-actions', 'notifications', appId || 'all'],
    queryFn: () => fetchNotifications(appId),
    refetchInterval: (query) => {
      const err = query.state.error as Error | null;
      if (err?.message?.startsWith('404')) return false;
      return POLL_MS;
    },
    retry: (failureCount, error) => {
      if ((error as Error)?.message?.startsWith('404')) return false;
      return failureCount < 2;
    },
    staleTime: 10_000,
  });
}

export function useDismissNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: dismissNotification,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agent-actions', 'notifications'] });
    },
  });
}
